import { useState } from 'react';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { Badge } from '@/components/data-display/Badge';
import { RequirePermission } from '@/components/auth';
import type { IncomingPaymentDto, IncomingStatusCode } from './types';
import { INCOMING_STATUS, INCOMING_STATUS_LABELS } from './types';
import { formatAmount, formatDate, formatDateTime, STATUS_BADGE } from './format';

interface IncomingDetailModalProps {
  payment: IncomingPaymentDto | null;
  receiverName: string | null;
  isOpen: boolean;
  onClose: () => void;
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
  isApproving: boolean;
  isRejecting: boolean;
}

export function IncomingDetailModal({
  payment,
  receiverName,
  isOpen,
  onClose,
  onApprove,
  onReject,
  isApproving,
  isRejecting,
}: IncomingDetailModalProps) {
  const [confirmAction, setConfirmAction] = useState<'approve' | 'reject' | null>(null);

  if (!payment) return null;

  const statusCode = payment.payment_status;
  const statusLabel = INCOMING_STATUS_LABELS[statusCode as IncomingStatusCode] ?? statusCode;
  const isPending = statusCode === INCOMING_STATUS.PENDING;

  return (
    <>
      <Modal
        isOpen={isOpen}
        title="Incoming Payment Details"
        onClose={onClose}
        className="max-w-xl"
        footer={
          <>
            {/* Defense-in-depth: route gate already requires MANAGE_INCOMING;
                the action wrappers make the per-button intent grep-able. */}
            <RequirePermission any={['MANAGE_INCOMING']}>
              <>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!isPending || isApproving}
                  onClick={() => setConfirmAction('approve')}
                  data-testid="incoming-detail-approve-btn"
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!isPending || isRejecting}
                  onClick={() => setConfirmAction('reject')}
                  data-testid="incoming-detail-reject-btn"
                >
                  Reject
                </Button>
              </>
            </RequirePermission>
            <Button variant="outline" onClick={onClose} data-testid="incoming-detail-close">
              Close
            </Button>
          </>
        }
      >
        <div className="space-y-4" data-testid="incoming-detail-content">
          {/* Header row */}
          <div className="flex items-center gap-3">
            <span data-testid="incoming-detail-status">
              <Badge variant={STATUS_BADGE[statusCode] ?? 'default'}>{statusLabel}</Badge>
            </span>
            <span className="text-[12px] text-[#999]" data-testid="incoming-detail-transaction-id">
              {payment.transaction_id ?? '—'}
            </span>
            <span className="ml-auto text-[12px] text-[#999]">
              {formatDate(payment.created_at)}
            </span>
          </div>

          {/* Receiver */}
          <Section title="Receiver">
            <DetailField label="Client Name" value={receiverName ?? '—'} />
            <DetailField label="Client ID" value={String(payment.client_id)} />
            <DetailField
              label="Client Account ID"
              value={payment.client_account_id != null ? String(payment.client_account_id) : '—'}
            />
          </Section>

          {/* Sender */}
          <Section title="Sender">
            <DetailField label="Name" value={payment.sender_name ?? '—'} />
            <DetailField label="Account" value={payment.sender_account ?? '—'} />
            <DetailField label="Bank" value={payment.sender_bank ?? '—'} />
            <DetailField label="SWIFT" value={payment.sender_swift ?? '—'} />
          </Section>

          {/* Payment */}
          <Section title="Payment">
            <DetailField
              label="Amount"
              value={formatAmount(payment.amount, payment.currency_code)}
            />
            <DetailField label="Currency" value={payment.currency_code} />
            <DetailField label="Reference" value={payment.reference ?? '—'} />
            <DetailField
              label="Received Date"
              value={payment.received_date ? formatDate(payment.received_date) : '—'}
            />
          </Section>

          {/* Audit */}
          <Section title="Audit">
            <DetailField label="Status ID" value={String(payment.payment_status_id)} />
            <DetailField label="Created" value={formatDateTime(payment.created_at)} />
            <DetailField label="Updated" value={formatDateTime(payment.updated_at)} />
          </Section>
        </div>
      </Modal>

      {confirmAction && (
        <ConfirmDialog
          isOpen
          title={confirmAction === 'approve' ? 'Approve Payment' : 'Reject Payment'}
          message={
            confirmAction === 'approve'
              ? `Approve incoming payment of ${formatAmount(payment.amount, payment.currency_code)} from ${payment.sender_name ?? 'Unknown'}? This will credit the client account.`
              : `Reject this incoming payment from ${payment.sender_name ?? 'Unknown'}?`
          }
          confirmText={confirmAction === 'approve' ? 'Approve' : 'Reject'}
          isDangerous={confirmAction === 'reject'}
          onConfirm={() => {
            if (confirmAction === 'approve') {
              onApprove(payment.incoming_payment_id);
            } else {
              onReject(payment.incoming_payment_id);
            }
            setConfirmAction(null);
            onClose();
          }}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-[12px] font-semibold uppercase tracking-[0.5px] text-[#666]">
        {title}
      </h3>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">{children}</div>
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]">
        {label}
      </div>
      <div className="mt-0.5 text-[13px] text-[#333]">{value}</div>
    </div>
  );
}
